import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  MessageSquare, RefreshCw, Lock, Send, User, Clock,
  Sparkles, Bug, Filter,
} from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { useLiveEvents } from "@/lib/useLiveEvents";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge, STATUS_META, relTime } from "./SupportPanel";
import TicketAttachments from "./TicketAttachments";

const KIND_ICON = { feature_request: Sparkles, bug_report: Bug };

/** Developer-only inbox — triage crew tickets, change status, add public /
 *  private comments. Reuses the crew-side badge + relative-time helpers so
 *  the visual language stays consistent across both sides. */
export default function DevSupportPanel() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("__all__");
  const [selectedId, setSelectedId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [replyPrivate, setReplyPrivate] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter && statusFilter !== "__all__" ? { params: { status: statusFilter } } : {};
      const { data } = await api.get("/admin/support/tickets", params);
      setTickets(data || []);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Could not load tickets");
    } finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  // Real-time inbox — every support.* WS event silently refreshes the list
  // so a newly-raised ticket, a status change, or a fresh crew comment
  // appears without hitting Refresh.
  useLiveEvents(useCallback((ev) => {
    if (ev && ev.type && ev.type.startsWith("support.")) load();
  }, [load]));

  const selected = useMemo(() => tickets.find((t) => t.id === selectedId) || null, [tickets, selectedId]);

  const setStatus = async (t, newStatus) => {
    try {
      await api.put(`/admin/support/tickets/${t.id}/status`, { status: newStatus });
      toast.success(`Marked ${STATUS_META[newStatus]?.label}`);
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Could not update status");
    }
  };

  const reply = async () => {
    const body = replyText.trim();
    if ((!body && replyAttachments.length === 0) || !selected) return;
    setSaving(true);
    try {
      await api.post(`/support/tickets/${selected.id}/comments`, {
        body: body || "(screenshot attached)",
        private: replyPrivate,
        attachments: replyAttachments,
      });
      setReplyText("");
      setReplyAttachments([]);
      setReplyPrivate(false);
      toast.success(replyPrivate ? "Private note added" : "Comment posted");
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Could not add comment");
    } finally { setSaving(false); }
  };

  const counts = useMemo(() => {
    const c = {};
    for (const t of tickets) c[t.status] = (c[t.status] || 0) + 1;
    return c;
  }, [tickets]);

  return (
    <div data-testid="dev-support-panel">
      <div className="flex justify-between items-start mb-6 flex-wrap gap-3">
        <div>
          <h2 className="font-display text-3xl">Support inbox</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Crew feature-requests & technical issues. Triage, comment (publicly or privately), and move each ticket through the pipeline.
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-56" data-testid="dev-support-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All statuses ({tickets.length})</SelectItem>
              {Object.entries(STATUS_META).map(([k, v]) => (
                <SelectItem key={k} value={k}>
                  {v.label}{counts[k] ? ` · ${counts[k]}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={load} data-testid="dev-support-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {loading && tickets.length === 0 ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : tickets.length === 0 ? (
        <div className="border border-dashed border-border p-10 text-center text-muted-foreground">
          <MessageSquare className="h-6 w-6 mx-auto mb-3 opacity-60" />
          <p className="text-sm">Inbox is clear.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6 items-start">
          <ul className="space-y-2 max-h-[70vh] overflow-y-auto pr-1" data-testid="dev-support-list">
            {tickets.map((t) => {
              const Icon = KIND_ICON[t.kind] || MessageSquare;
              const active = t.id === selectedId;
              return (
                <li key={t.id}>
                  <button
                    onClick={() => setSelectedId(t.id)}
                    className={`w-full text-left border p-3 transition-colors ${active ? "border-primary bg-primary/5" : "border-border hover:border-primary/60"}`}
                    data-testid={`dev-support-item-${t.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <Icon className="h-4 w-4 mt-1 text-accent flex-shrink-0" strokeWidth={1.75} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm truncate">{t.subject}</span>
                          <StatusBadge status={t.status} />
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-1 flex flex-wrap gap-2">
                          <span className="font-mono">{t.short_id}</span>
                          <span>· {t.created_by_email}</span>
                        </div>
                        <div className="text-[10px] text-muted-foreground/70 mt-0.5">Updated {relTime(t.updated_at)}</div>
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>

          <div className="border border-border bg-card p-6 min-h-[400px]" data-testid="dev-support-detail">
            {!selected ? (
              <div className="h-full flex items-center justify-center text-sm text-muted-foreground italic">
                Select a ticket to review.
              </div>
            ) : (
              <div className="space-y-6">
                <div>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <h3 className="font-display text-2xl min-w-0">{selected.subject}</h3>
                    <StatusBadge status={selected.status} testId="dev-support-detail-status" />
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-3">
                    <span className="font-mono">{selected.short_id}</span>
                    <span>{selected.kind === "feature_request" ? "Upgrade / feature request" : "Technical issue"}</span>
                    <span>From <span className="text-foreground/85">{selected.created_by_email}</span> · <span className="italic">{selected.created_by_role}</span></span>
                    <span>Opened {relTime(selected.created_at)}</span>
                  </div>
                </div>

                <div>
                  <Label className="overline text-primary/60">Set status</Label>
                  <div className="mt-2 flex flex-wrap gap-2" data-testid="dev-support-status-actions">
                    {Object.entries(STATUS_META).map(([k, v]) => (
                      <Button
                        key={k}
                        size="sm"
                        variant={selected.status === k ? "default" : "outline"}
                        onClick={() => setStatus(selected, k)}
                        className={`rounded-full text-[11px] ${selected.status === k ? "bg-primary hover:bg-primary/90" : ""}`}
                        data-testid={`dev-support-set-${k}`}
                      >
                        {v.label}
                      </Button>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="overline text-primary/60 mb-3">Original message</div>
                  <div className="border border-border bg-muted/40 p-4 text-sm whitespace-pre-wrap">{selected.body}</div>
                  {(selected.attachments || []).length > 0 && (
                    <div className="mt-3">
                      <TicketAttachments paths={selected.attachments} readOnly testId="dev-support-orig-attachments" />
                    </div>
                  )}
                </div>

                <div>
                  <div className="overline text-primary/60 mb-3">Conversation</div>
                  {(selected.comments || []).length === 0 ? (
                    <div className="text-xs text-muted-foreground italic">No replies yet.</div>
                  ) : (
                    <ul className="space-y-3">
                      {selected.comments.map((c) => {
                        const fromDev = c.author_role === "developer";
                        return (
                          <li
                            key={c.id}
                            className={`border p-3 ${c.private ? "border-amber-500/40 bg-amber-500/5" : fromDev ? "border-accent/40 bg-accent/5" : "border-border bg-card"}`}
                            data-testid={`dev-support-comment-${c.id}`}
                          >
                            <div className="flex items-center gap-2 mb-1 text-[11px] text-muted-foreground">
                              <User className="h-3 w-3" />
                              <span className={fromDev ? "text-accent font-medium" : ""}>
                                {fromDev ? "You (developer)" : c.author_email}
                              </span>
                              <span>·</span>
                              <Clock className="h-3 w-3" />
                              <span>{relTime(c.created_at)}</span>
                              {c.private && (
                                <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400 ml-2">
                                  <Lock className="h-3 w-3" /> private note (dev only)
                                </span>
                              )}
                            </div>
                            <div className="text-sm whitespace-pre-wrap">{c.body}</div>
                            {(c.attachments || []).length > 0 && (
                              <div className="mt-2">
                                <TicketAttachments paths={c.attachments} readOnly testId={`dev-support-comment-attachments-${c.id}`} />
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>

                <div className="border-t border-border pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="overline text-primary/60">Add a comment</Label>
                    <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="dev-support-private-toggle">
                      <input
                        type="checkbox"
                        checked={replyPrivate}
                        onChange={(e) => setReplyPrivate(e.target.checked)}
                        className="h-3.5 w-3.5 accent-amber-500"
                      />
                      <Lock className="h-3 w-3 text-amber-600" />
                      <span className="text-muted-foreground">Private note (dev only)</span>
                    </label>
                  </div>
                  <Textarea
                    rows={3}
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder={replyPrivate ? "Internal note — the requester will NOT see this." : "Reply to the requester — visible on their support panel."}
                    className={replyPrivate ? "border-amber-500/40 focus-visible:ring-amber-500/40" : ""}
                    data-testid="dev-support-reply-body"
                  />
                  <div className="mt-2">
                    <TicketAttachments
                      paths={replyAttachments}
                      onChange={setReplyAttachments}
                      testId="dev-support-reply-attachments"
                    />
                  </div>
                  <div className="flex justify-end mt-2">
                    <Button
                      onClick={reply}
                      disabled={saving || (!replyText.trim() && replyAttachments.length === 0)}
                      className={`rounded-full ${replyPrivate ? "bg-amber-600 hover:bg-amber-700 text-white" : "bg-primary hover:bg-primary/90"}`}
                      data-testid="dev-support-reply-submit"
                    >
                      <Send className="h-3.5 w-3.5 mr-2" />
                      {saving ? "Sending…" : replyPrivate ? "Save private note" : "Send comment"}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
